import React, { useCallback, memo } from 'react';
import TableCell from '@mui/material/TableCell';
import { IconButton, styled } from '@mui/material';
import { Column } from '../../../../components/table/types';
import { DIRECTIONS } from '../../../../constants';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { SortOptions } from '../../../../types';

const StyledSortIcon = styled(ArrowUpwardIcon)({
    width: '17px',
    height: '17px',
    position: 'relative',
    bottom: '1px',
});

interface DataTableCellProps extends Column {
    sortOptions: SortOptions | null;
    setSorted?: (sortOptions: SortOptions | null) => void;
    setSortOptions: React.Dispatch<React.SetStateAction<SortOptions | null>>;
}

const DataTableCell: React.FC<DataTableCellProps> = ({
    isSorted,
    id,
    width,
    alignHeader,
    sticky,
    title,
    sortOptions,
    setSorted,
    setSortOptions,
}) => {
    const isActive = sortOptions && sortOptions.columnId === id;
    const isDesc = isActive && sortOptions.direction === DIRECTIONS.DESC;

    const handleSort = useCallback(
        (columnId: string) => {
            if (sortOptions && sortOptions.columnId === columnId) {
                if (sortOptions.direction === DIRECTIONS.DESC) {
                    setSortOptions(null);
                    if (setSorted) {
                        setSorted(null);
                    }
                } else {
                    const newSortOptions = { columnId, direction: DIRECTIONS.DESC };
                    setSortOptions(newSortOptions);
                    if (setSorted) {
                        setSorted(newSortOptions);
                    }
                }
            } else {
                const newSortOptions = { columnId, direction: DIRECTIONS.ASC };
                setSortOptions(newSortOptions);
                if (setSorted) {
                    setSorted(newSortOptions);
                }
            }
        },
        [setSorted, sortOptions, setSortOptions]
    );

    const onTableCellClick = useCallback(() => {
        if (isSorted) {
            handleSort(id);
        }
    }, [handleSort, id, isSorted]);

    return (
        <TableCell
            data-testid='table-cell'
            key={id}
            sx={{
                padding: '0 16px',
                height: '44px',
                width: width || 200,
                maxWidth: width || 200,
                minWidth: width || 200,
                textAlign: alignHeader || 'left',
                ...(sticky && {
                    position: 'sticky',
                    left: sticky === 'left' ? 0 : 'unset',
                    right: sticky === 'right' ? 0 : 'unset',
                    zIndex: 3,
                }),
                ...(isSorted && {
                    cursor: 'pointer',
                    '& .MuiSvgIcon-root': {
                        transition: 'all 0.2s',
                        color: isActive ? 'common.grey1' : 'transparent',
                        transform: isDesc ? 'rotate(180deg)' : 'initial',
                    },
                    '&:hover': {
                        '& .MuiSvgIcon-root': {
                            color: isActive ? 'common.grey1' : 'common.grey2',
                        },
                    },
                }),
            }}
            onClick={onTableCellClick}
        >
            {title || id}
            {isSorted && (
                <IconButton sx={{ margin: '0 8px', mp: 0, padding: '3px' }}>
                    <StyledSortIcon />
                </IconButton>
            )}
        </TableCell>
    );
};

export default memo(DataTableCell);
